import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { X, ChevronDown, ChevronRight, CalendarClock, Users, History, Pencil, Trash2, ListChecks, NotebookPen } from 'lucide-react';
import { Modal, Button, Select } from '../../../components';
import carePlannerService from '../../../services/carehome_manager/carePlannerService';
import {
  Badge, stateBadge, PRIORITY_BADGE, FieldLabel, inputClass, longDayLabel, clock, stamp, rotaWarning,
  carerNames, errorMessage, toMinutes, visitStatus,
} from './shared';

/**
 * One care visit: everything planned together for a resident at one time —
 * its tasks, who is responsible, and what was recorded.
 *
 * The tasks are kept separate underneath (each has its own outcome, and the
 * carer records the visit as one care note that closes each of them), so the
 * visit-wide controls here act on every task they apply to:
 *
 *   allocation        every task not yet recorded
 *   change this date  every task nobody has started — time, priority, instructions
 *   record missed     every task not recorded whose time has come
 *   review            every task awaiting review
 *   remove this date  every task nobody has started (or one task, from its row)
 *
 * Recorded care is never editable here: it is the record of what happened.
 */

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-[#EDF1F7] dark:border-gray-800 last:border-0">
    <dt className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400 shrink-0">{label}</dt>
    <dd className="text-[12.5px] font-semibold text-[#0B1220] dark:text-gray-100 text-right break-words min-w-0">{children}</dd>
  </div>
);

const Section = ({ icon: Icon, title, children, className = '' }) => (
  <section className={`rounded-[14px] border border-[#E3E9F3] dark:border-gray-800 bg-white dark:bg-[#262b33] p-4 ${className}`}>
    <h3 className="flex items-center gap-2 text-[13px] font-bold text-[#0B1220] dark:text-gray-100 mb-3">
      {Icon && <Icon className="w-4 h-4 text-blue-600" />}
      {title}
    </h3>
    {children}
  </section>
);

const earliest = (values) => values.filter(Boolean).sort()[0] || null;
const latest = (values) => values.filter(Boolean).sort().slice(-1)[0] || null;

/** Run one call per task, in turn. Returns the updated tasks; throws on the first failure. */
async function eachTask(tasks, call) {
  const updated = [];
  for (const task of tasks) {
    // eslint-disable-next-line no-await-in-loop
    const res = await call(task);
    if (res?.data) updated.push(res.data);
  }
  return updated;
}

const VisitDetailModal = ({ visit, meta, rota, preset, onClose, onChanged, onRemoved, onEditPlan }) => {
  const { tasks, first } = visit;
  const carers = meta?.carers || [];
  const open = tasks.filter((t) => t.open);
  const unstarted = open.filter((t) => !t.started_at);
  const reviewable = tasks.filter((t) => t.needs_review);
  const missable = open.filter((t) => t.overdue || t.due_now || t.state === 'in_progress');
  const many = tasks.length > 1;

  // `preset` comes from dropping the visit on a carer's row: their name is
  // pre-selected, and the manager confirms with Assign care.
  const [assignedTo, setAssignedTo] = useState(String(preset?.carerId ?? first.assigned_to ?? ''));
  const [secondAssignee, setSecondAssignee] = useState(String(preset?.secondId ?? first.second_assignee ?? ''));
  const [staffRequired, setStaffRequired] = useState(String(first.staff_required));
  const [notify, setNotify] = useState(true);
  const [editing, setEditing] = useState(false);
  const [details, setDetails] = useState({
    start: first.start_time, end: first.end_time, priority: first.priority, instructions: first.instructions,
  });
  const [missedOpen, setMissedOpen] = useState(false);
  const [missedReason, setMissedReason] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [confirm, setConfirm] = useState(null); // 'visit' | 'plan' | task id
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const two = staffRequired === '2';
  const priority = PRIORITY_BADGE[visit.priority];
  const status = visitStatus(visit) || 'Scheduled';
  const allocationChanged = open.some((t) => String(t.assigned_to ?? '') !== assignedTo
    || String(t.second_assignee ?? '') !== (two ? secondAssignee : '')
    || String(t.staff_required) !== staffRequired);

  const carerOptions = [
    { value: '', label: 'Unassigned' },
    ...carers.map((c) => ({ value: String(c.id), label: `${c.name} · ${c.job_title}` })),
  ];
  // A carer since archived is still shown by name rather than as a blank select.
  [first.assigned_to, first.second_assignee].forEach((id, i) => {
    if (id && !carerOptions.some((o) => o.value === String(id))) {
      carerOptions.push({ value: String(id), label: (i === 0 ? first.assigned_name : first.second_name) || 'Former staff member' });
    }
  });

  const warnings = useMemo(() => [assignedTo, two ? secondAssignee : '']
    .filter(Boolean)
    .map((id) => rotaWarning(rota, carers.find((c) => String(c.id) === id), [first.task_date], first.start_time, first.end_time))
    .filter(Boolean), [assignedTo, secondAssignee, two, rota, carers, first]);

  const instructions = [...new Set(tasks.map((t) => t.instructions).filter(Boolean))];
  const history = useMemo(() => {
    const seen = new Set();
    return tasks
      .flatMap((t) => (Array.isArray(t.history) ? t.history : []).map((h) => ({ ...h, label: t.action_label })))
      .filter((h) => {
        // "Planned by …" is written once per task; show it once for the visit.
        const key = `${h.at}|${h.text}`;
        if (h.type === 'created' && seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => String(b.at).localeCompare(String(a.at)));
  }, [tasks]);

  const run = async (key, fn, message) => {
    setBusy(key);
    setError('');
    try {
      const updated = await fn();
      if (message) toast.success(message);
      return updated;
    } catch (err) {
      setError(errorMessage(err));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const saveAllocation = async () => {
    if (two && Boolean(assignedTo) !== Boolean(secondAssignee)) {
      setError('Two-person care needs both carers named — or leave both unassigned.');
      return;
    }
    if (two && assignedTo && assignedTo === secondAssignee) {
      setError('Choose two different carers.');
      return;
    }
    const updated = await run('allocate', () => eachTask(open, (t) => carePlannerService.updateTask(t.id, {
      staff_required: t.started_at ? undefined : Number(staffRequired),
      assigned_to: assignedTo ? Number(assignedTo) : null,
      second_assignee: two && secondAssignee ? Number(secondAssignee) : null,
      notify,
    })), many ? 'Allocation saved for the visit.' : 'Allocation saved.');
    if (updated) onChanged(updated);
  };

  const saveDetails = async () => {
    const s = toMinutes(details.start);
    const e = toMinutes(details.end);
    if (s === null || e === null || e <= s) {
      setError('The end time must be after the start time.');
      return;
    }
    if (!details.instructions.trim()) {
      setError('Add instructions for this resident.');
      return;
    }
    const updated = await run('details', () => eachTask(unstarted, (t) => carePlannerService.updateTask(t.id, {
      start_time: details.start, end_time: details.end, priority: details.priority, instructions: details.instructions, notify: false,
    })), 'Care updated for this date.');
    if (updated) {
      setEditing(false);
      onChanged(updated);
    }
  };

  const saveMissed = async () => {
    if (!missedReason.trim()) {
      setError('Give the reason the care was missed.');
      return;
    }
    const updated = await run('missed', () => eachTask(missable, (t) => carePlannerService.markMissed(t.id, missedReason)),
      'Recorded as missed care.');
    if (updated) onChanged(updated);
  };

  const saveReview = async () => {
    const updated = await run('review', () => eachTask(reviewable, (t) => carePlannerService.reviewTask(t.id, reviewNote)),
      'Marked as reviewed.');
    if (updated) onChanged(updated);
  };

  const removeTasks = async (list, message) => {
    const done = await run('remove', () => eachTask(list, async (t) => { await carePlannerService.deleteTask(t.id); return null; }), message);
    if (done) onRemoved();
  };
  const removePlan = async () => {
    const res = await run('removePlan', () => carePlannerService.deletePlan(first.plan_id));
    if (res) {
      toast.success(res.message || 'Removed.');
      onRemoved();
    }
  };

  const recordedAt = latest(tasks.map((t) => t.completed_at));
  const recorders = [...new Set(tasks.map((t) => t.recorded_by_name).filter(Boolean))].join(', ');
  const timing = visit.tone === 'missed'
    ? (reviewable.length ? 'Missed — needs review' : 'Missed — reviewed')
    : !open.length
      ? (visit.late ? 'Recorded after the planned window' : 'Within the planned window')
      : open.some((t) => t.overdue) ? 'Window passed — not all recorded yet' : 'Not recorded yet';

  return (
    <Modal isOpen onClose={onClose} hideHeader maxWidth="max-w-4xl" bodyClassName="flex flex-col">
      <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-[#EDF1F7] dark:border-gray-800">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <Badge tone={many ? (visit.tone === 'completed' ? 'green' : visit.tone === 'missed' ? 'red' : visit.tone === 'overdue' || visit.tone === 'not_done' ? 'amber' : 'blue') : stateBadge(first).tone}>
              {many ? status : stateBadge(first).label}
            </Badge>
            {priority && <Badge tone={priority.tone}>{priority.label}</Badge>}
            {visit.needs_allocation && <Badge tone="purple">Needs a carer</Badge>}
            {visit.late && <Badge tone="amber">Late</Badge>}
            {visit.needs_review && <Badge tone="red">Needs review</Badge>}
            {!many && <Badge tone="gray">{first.category}</Badge>}
          </div>
          <h2 className="text-[21px] font-extrabold tracking-[-0.3px] text-[#0B1220] dark:text-gray-100">
            {many ? `${tasks.length} care tasks` : first.action_label}
          </h2>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">
            <strong className="text-[#334155] dark:text-gray-200">{first.resident?.name}</strong>
            {first.resident?.room ? ` · Room ${first.resident.room}` : ''}
            {' · '}{longDayLabel(first.task_date)} · {first.start_time}–{first.end_time}
            {first.staff_required === 2 ? ' · 2 staff required' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-9 h-9 shrink-0 grid place-items-center rounded-full border border-[#DCE4EF] dark:border-gray-700 text-[#64748B] hover:text-[#0B1220] dark:hover:text-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      <div className="px-6 py-5 bg-[#F8FAFE] dark:bg-[#1f232a] grid md:grid-cols-[1.15fr_0.85fr] gap-4">
        <div className="space-y-4 min-w-0">
          <Section icon={ListChecks} title={many ? 'Care tasks in this visit' : 'Care task'}>
            <ul className="divide-y divide-[#EDF1F7] dark:divide-gray-800">
              {tasks.map((t) => {
                const b = stateBadge(t);
                return (
                  <li key={t.id} className="py-2.5 first:pt-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100">{t.action_label}</span>
                          <Badge tone={b.tone}>{b.label}</Badge>
                          {t.care_note_id && <Badge tone="blue"><NotebookPen className="w-3 h-3" /> In care note</Badge>}
                        </div>
                        <div className="text-[11.5px] text-[#94A3B8] mt-0.5">{t.category}</div>
                        {t.outcome_notes && <p className="text-[12px] text-[#334155] dark:text-gray-300 mt-1">{t.outcome_notes}</p>}
                        {t.outcome_reason && <p className="text-[12px] text-amber-700 dark:text-amber-400 mt-0.5">Reason: {t.outcome_reason}</p>}
                        {t.missed_reason && <p className="text-[12px] text-rose-600 dark:text-rose-400 mt-0.5">Missed: {t.missed_reason}</p>}
                        {t.completed_at && (
                          <p className="text-[11px] text-[#94A3B8] mt-0.5">
                            Recorded {stamp(t.completed_at)}{t.recorded_by_name ? ` · ${t.recorded_by_name}` : ''}{t.late ? ' · late' : ''}
                          </p>
                        )}
                      </div>
                      {many && !t.locked && (
                        confirm === t.id ? (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Button size="sm" variant="secondary" onClick={() => setConfirm(null)}>Keep</Button>
                            <Button size="sm" variant="danger" loading={busy === 'remove'} onClick={() => removeTasks([t], `${t.action_label} removed from this date.`)}>Remove</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" icon={<Trash2 className="w-3.5 h-3.5" />} aria-label={`Remove ${t.action_label} from this date`} onClick={() => setConfirm(t.id)} />
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {open.length > 0 && (
              <p className="mt-3 text-[11.5px] text-[#94A3B8]">
                The carer records {many ? 'the visit' : 'this'} as a care note in My Shift — {many ? 'each task is closed from its action in that note' : 'the outcome comes from that note'}.
              </p>
            )}
          </Section>

          <Section title="Instructions">
            {editing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <FieldLabel htmlFor="vd-start">Start</FieldLabel>
                    <input id="vd-start" type="time" className={inputClass} value={details.start} onChange={(e) => setDetails((d) => ({ ...d, start: e.target.value }))} />
                  </div>
                  <div>
                    <FieldLabel htmlFor="vd-end">End</FieldLabel>
                    <input id="vd-end" type="time" className={inputClass} value={details.end} onChange={(e) => setDetails((d) => ({ ...d, end: e.target.value }))} />
                  </div>
                  <div>
                    <FieldLabel>Priority</FieldLabel>
                    <Select
                      value={details.priority}
                      onChange={(v) => setDetails((d) => ({ ...d, priority: v }))}
                      options={(meta?.priorities || []).map((p) => ({ value: p.value, label: p.label }))}
                      className="w-full"
                    />
                  </div>
                </div>
                <textarea
                  rows={5}
                  maxLength={2000}
                  aria-label="Instructions"
                  className={`${inputClass} h-auto py-2.5 resize-y leading-relaxed`}
                  value={details.instructions}
                  onChange={(e) => setDetails((d) => ({ ...d, instructions: e.target.value }))}
                />
                <p className="text-[11.5px] text-[#94A3B8]">
                  Changes apply to this date only{many ? `, for the ${unstarted.length} task${unstarted.length === 1 ? '' : 's'} not yet started` : ''}. The carer will see the new instructions as unread.
                </p>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button size="sm" variant="primary" loading={busy === 'details'} onClick={saveDetails}>Save this date</Button>
                </div>
              </div>
            ) : (
              <>
                {instructions.map((text) => (
                  <p key={text} className="text-[13px] leading-relaxed text-[#334155] dark:text-gray-300 whitespace-pre-wrap mb-2 last:mb-0">{text}</p>
                ))}
                {unstarted.length > 0 && (
                  <button type="button" onClick={() => setEditing(true)} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:underline">
                    <Pencil className="w-3.5 h-3.5" /> Change this date only
                  </button>
                )}
              </>
            )}
          </Section>

          <Section icon={Users} title="Allocation">
            {open.length ? (
              <div className="space-y-3">
                {!open.some((t) => t.started_at) && (
                  <div>
                    <FieldLabel>Staff required</FieldLabel>
                    <Select
                      value={staffRequired}
                      onChange={(v) => { setStaffRequired(v); if (v === '1') setSecondAssignee(''); }}
                      options={[{ value: '1', label: '1 care worker' }, { value: '2', label: '2 care workers' }]}
                      className="w-full"
                    />
                  </div>
                )}
                <div className={two ? 'grid grid-cols-2 gap-3' : ''}>
                  <div>
                    <FieldLabel>{two ? 'First carer' : 'Carer'}</FieldLabel>
                    <Select value={assignedTo} onChange={setAssignedTo} options={carerOptions} className="w-full" />
                  </div>
                  {two && (
                    <div>
                      <FieldLabel>Second carer</FieldLabel>
                      <Select value={secondAssignee} onChange={setSecondAssignee} options={carerOptions.filter((o) => !o.value || o.value !== assignedTo)} className="w-full" />
                    </div>
                  )}
                </div>
                {warnings.map((w) => (
                  <p key={w} className="text-[12px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/40 rounded-lg px-3 py-2">{w}</p>
                ))}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <label className="flex items-center gap-2 text-[12.5px] font-semibold text-[#334155] dark:text-gray-300 cursor-pointer">
                    <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="w-4 h-4 accent-blue-600" />
                    Notify newly assigned carer(s)
                  </label>
                  <Button variant="primary" size="sm" disabled={!allocationChanged} loading={busy === 'allocate'} onClick={saveAllocation}>
                    {first.assigned_to ? 'Save reassignment' : 'Assign care'}
                  </Button>
                </div>
                <p className="text-[11.5px] text-[#94A3B8]">
                  Changes who is responsible for {many ? 'this visit' : 'this task'} on this date only. The previous allocation stays in the history.
                </p>
              </div>
            ) : (
              <p className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100">{carerNames(first) || 'Unassigned'}</p>
            )}
          </Section>

          {history.length > 0 && (
            <section className="rounded-[14px] border border-[#E3E9F3] dark:border-gray-800 bg-white dark:bg-[#262b33]">
              <button type="button" onClick={() => setHistoryOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-[13px] font-bold text-[#0B1220] dark:text-gray-100">
                <History className="w-4 h-4 text-blue-600" />
                <span className="flex-1 text-left">History ({history.length})</span>
                {historyOpen ? <ChevronDown className="w-4 h-4 text-[#94A3B8]" /> : <ChevronRight className="w-4 h-4 text-[#94A3B8]" />}
              </button>
              {historyOpen && (
                <ol className="px-4 pb-4 space-y-2.5">
                  {history.map((h, i) => (
                    <li key={`${h.at}-${i}`} className="text-[12px] text-[#334155] dark:text-gray-300 border-l-2 border-[#DCE6F5] dark:border-gray-700 pl-3">
                      <div className="font-semibold">{many && h.type !== 'created' ? `${h.label}: ` : ''}{h.text}</div>
                      <div className="text-[11px] text-[#94A3B8]">{stamp(h.at)}{h.by_name ? ` · ${h.by_name}` : ''}</div>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )}
        </div>

        <div className="space-y-4 min-w-0">
          <Section icon={CalendarClock} title="Care delivery">
            <dl>
              <Row label="Status">{many ? status : stateBadge(first).label}</Row>
              <Row label="Planned time">{first.start_time}–{first.end_time}</Row>
              <Row label="Instructions read">{clock(earliest(tasks.map((t) => t.acknowledged_at))) || 'Not yet'}</Row>
              <Row label="Started">{clock(earliest(tasks.map((t) => t.started_at))) || '—'}</Row>
              <Row label="Recorded">{recordedAt ? `${stamp(recordedAt)}${recorders ? ` · ${recorders}` : ''}` : '—'}</Row>
              {many && <Row label="Tasks done">{visit.recorded.length} of {tasks.length}</Row>}
              <Row label="Timing">{timing}</Row>
            </dl>
          </Section>

          {(reviewable.length > 0 || tasks.some((t) => t.reviewed_at)) && (
            <Section title="Manager review">
              {reviewable.length ? (
                <div className="space-y-2.5">
                  <p className="text-[12px] text-[#64748B] dark:text-gray-400">
                    {reviewable.map((t) => t.action_label).join(', ')}
                    {' — '}
                    {reviewable.some((t) => t.state === 'missed')
                      ? 'review the recorded reason and add any follow-up. Reviewing keeps the care marked as missed.'
                      : 'recorded late or not fully given.'}
                  </p>
                  <textarea
                    rows={2}
                    maxLength={1000}
                    aria-label="Review note"
                    placeholder="Manager note (optional)"
                    className={`${inputClass} h-auto py-2 resize-y`}
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button size="sm" variant="primary" loading={busy === 'review'} onClick={saveReview}>Mark reviewed</Button>
                  </div>
                </div>
              ) : (
                <div className="text-[12.5px] text-[#334155] dark:text-gray-300 space-y-1">
                  {tasks.filter((t) => t.reviewed_at).map((t) => (
                    <p key={t.id}><strong>{many ? `${t.action_label}: ` : ''}</strong>Reviewed by {t.reviewed_by_name || 'a manager'} · {stamp(t.reviewed_at)}{t.review_note ? ` — ${t.review_note}` : ''}</p>
                  ))}
                </div>
              )}
            </Section>
          )}

          {missable.length > 0 && (
            <Section title="Missed care">
              {missedOpen ? (
                <div className="space-y-2.5">
                  <p className="text-[12px] text-[#64748B] dark:text-gray-400">
                    Record confirmed missed care with a reason{many ? ` — for the ${missable.length} task${missable.length === 1 ? '' : 's'} not recorded` : ''}. Overdue care alone does not confirm it was missed — the carer may still record it late.
                  </p>
                  <textarea
                    rows={3}
                    maxLength={1000}
                    aria-label="Reason for missed care"
                    placeholder="Why was this care missed?"
                    className={`${inputClass} h-auto py-2 resize-y`}
                    value={missedReason}
                    onChange={(e) => setMissedReason(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setMissedOpen(false)}>Cancel</Button>
                    <Button size="sm" variant="danger" loading={busy === 'missed'} onClick={saveMissed}>Save missed care</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="danger-soft" onClick={() => setMissedOpen(true)}>Record as missed</Button>
              )}
            </Section>
          )}
        </div>
      </div>

      {error && <p role="alert" className="px-6 pt-3 text-[13px] font-medium text-rose-600">{error}</p>}

      <footer className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-t border-[#EDF1F7] dark:border-gray-800">
        <div className="flex flex-wrap items-center gap-2">
          {confirm === 'visit' || confirm === 'plan' ? (
            <>
              <span className="text-[12.5px] font-semibold text-rose-700 dark:text-rose-300">
                {confirm === 'visit'
                  ? `Remove ${many ? 'this visit' : 'this task'} on this date?`
                  : 'Remove every upcoming task in this plan? Recorded care is kept.'}
              </span>
              <Button size="sm" variant="secondary" onClick={() => setConfirm(null)}>Keep</Button>
              <Button
                size="sm"
                variant="danger"
                loading={busy === 'remove' || busy === 'removePlan'}
                onClick={confirm === 'visit' ? () => removeTasks(tasks.filter((t) => !t.locked), 'Removed from this date.') : removePlan}
              >
                Remove
              </Button>
            </>
          ) : (
            <>
              {tasks.some((t) => !t.locked) && (
                <Button size="sm" variant="ghost" leftIcon={<Trash2 className="w-3.5 h-3.5" />} onClick={() => setConfirm('visit')}>Remove this date</Button>
              )}
              <Button size="sm" variant="ghost" leftIcon={<Trash2 className="w-3.5 h-3.5" />} onClick={() => setConfirm('plan')}>Remove rest of plan</Button>
            </>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => onEditPlan(first.plan_id)} leftIcon={<Pencil className="w-4 h-4" />}>Edit planned care</Button>
          <Button variant="primary" onClick={onClose}>Done</Button>
        </div>
      </footer>
    </Modal>
  );
};

export default VisitDetailModal;
